import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDateID, formatTimeID } from "@/lib/format";
import { toast } from "sonner";
import {
  Wallet, Landmark, Coins, ArrowDownCircle, ArrowUpCircle, RotateCcw, Scale,
  FileText, FileSpreadsheet, CheckCircle2, XCircle, Eye, X, PiggyBank, Calculator,
} from "lucide-react";
import { localISO, MutModal } from "./components";

const SEGMENTS = [
  { id: "today", label: "Hari Ini" },
  { id: "day", label: "Tanggal" },
  { id: "week", label: "Minggu" },
  { id: "month", label: "Bulan" },
  { id: "range", label: "Rentang" },
];

const TYPE_META = {
  sale: { label: "Penjualan", cls: "bg-sky-50 text-sky-600" },
  cash_in: { label: "Masuk", cls: "bg-emerald-50 text-emerald-600" },
  cash_out: { label: "Keluar", cls: "bg-red-50 text-red-500" },
  deposit: { label: "Setoran", cls: "bg-blue-50 text-blue-600" },
  refund_cash: { label: "Refund", cls: "bg-orange-50 text-orange-500" },
  adjustment: { label: "Penyesuaian", cls: "bg-purple-50 text-purple-600" },
};

const BAL_META = {
  balance: { label: "Balance • Selisih Rp 0", cls: "bg-emerald-50 text-emerald-600 border-emerald-200" },
  lebih: { label: "Selisih Lebih", cls: "bg-amber-50 text-amber-600 border-amber-200" },
  kurang: { label: "Selisih Kurang", cls: "bg-red-50 text-red-600 border-red-200" },
};

function segRange(seg, day, from, to) {
  const today = localISO(new Date());
  if (seg === "today") return { from: today, to: today };
  if (seg === "day") return { from: day, to: day };
  if (seg === "week") {
    const d = new Date(today + "T00:00:00Z");
    const mon = new Date(d);
    mon.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    const sun = new Date(mon);
    sun.setUTCDate(mon.getUTCDate() + 6);
    return { from: mon.toISOString().slice(0, 10), to: sun.toISOString().slice(0, 10) };
  }
  if (seg === "month") {
    const ym = today.slice(0, 7);
    const last = String(new Date(Date.UTC(Number(today.slice(0, 4)), Number(today.slice(5, 7)), 0)).getUTCDate()).padStart(2, "0");
    return { from: `${ym}-01`, to: `${ym}-${last}` };
  }
  return { from, to };
}

const signed = (v) => `${v >= 0 ? "+" : "-"}${formatIDR(Math.abs(v))}`;
const inp = "h-9 px-2.5 rounded-lg border border-rose-200 text-xs text-slate-700 bg-white focus:outline-none focus:border-rose-400";

// Versi standalone untuk kasir (tanpa AdminLayout) — diakses via /pos/reports
export function POSReportsKasir() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#FBF7F4]">
      <div className="bg-white border-b border-rose-100 px-4 py-3 flex items-center gap-3">
        <button
          data-testid="pos-kasir-reports-back"
          onClick={() => nav("/pos")}
          className="h-9 px-3.5 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 transition-colors"
        >
          Kembali ke Kasir
        </button>
        <span className="font-display font-bold text-slate-900 text-sm">Laporan Penjualan</span>
      </div>
      <div className="p-4 lg:p-6">
        <POSReports />
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-slate-50 last:border-0 text-xs">
      <span className="text-slate-500 flex-shrink-0">{label}</span>
      <span className="font-medium text-slate-800 text-right">{children}</span>
    </div>
  );
}

export default function POSReports() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const canPickCashier = ["owner", "supervisor", "kasir"].includes(user?.role);
  const [seg, setSeg] = useState("today");
  const [day, setDay] = useState(localISO(new Date()));
  const [rangeFrom, setRangeFrom] = useState(localISO(new Date()));
  const [rangeTo, setRangeTo] = useState(localISO(new Date()));
  const [cashierId, setCashierId] = useState("");
  const [summary, setSummary] = useState(null);
  const [muts, setMuts] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [modal, setModal] = useState(null);
  const [detail, setDetail] = useState(null);
  const [shiftDetail, setShiftDetail] = useState(null);

  const range = useMemo(() => segRange(seg, day, rangeFrom, rangeTo), [seg, day, rangeFrom, rangeTo]);
  const effCashier = canPickCashier ? cashierId : user?.id;

  const load = () => {
    const params = { date_from: range.from, date_to: range.to };
    if (effCashier) params.cashier_id = effCashier;
    api.get("/pos/cash/summary", { params }).then((r) => setSummary(r.data));
    api.get("/pos/cash/mutations", { params }).then((r) => setMuts(r.data));
    api.get("/pos/shifts", { params }).then((r) => setShifts(r.data));
    const sparams = { date_from: range.from, date_to: range.to };
    if (effCashier) sparams.employee_id = effCashier;
    api.get("/pos/attendant/sessions", { params: sparams }).then((r) => setSessions(r.data)).catch(() => {});
    api.get("/pos/handovers", { params }).then((r) => setHandovers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [range.from, range.to, effCashier]);

  const reviewAdj = async (id, action) => {
    try {
      await api.post(`/pos/cash/adjustments/${id}/review`, { action });
      toast.success(action === "approve" ? "Penyesuaian disetujui" : "Penyesuaian ditolak");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses"); }
  };

  const exportExcel = async () => {
    try {
      const params = { date_from: range.from, date_to: range.to };
      if (effCashier) params.cashier_id = effCashier;
      const res = await api.get("/pos/cash/export.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-kas-${range.from}-sd-${range.to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("File Excel diunduh");
    } catch { toast.error("Gagal mengunduh Excel"); }
  };

  const exportPDF = () => {
    if (!summary) return;
    const kasirLabel = effCashier ? (summary.cashiers.find((c) => c.id === effCashier)?.name || "Kasir") : "Semua Kasir";
    const card = (l, v) => `<div class="card"><span class="lbl">${l}</span><b>${formatIDR(v)}</b></div>`;
    const cardHtml = card("Penjualan Cash Tunai", summary.cash_sales) + card("Uang Masuk Lainnya", summary.cash_in)
      + card("Pengeluaran Kas", summary.cash_out) + card("Uang Disetor", summary.deposits)
      + card("Saldo Kas Berjalan", summary.saldo_berjalan) + card("Saldo Kembalian (Tutup Kasir)", summary.keep_total);
    const rows = [...muts].reverse().map((e) => `<tr>
      <td>${formatDateID(e.time)} ${formatTimeID(e.time)}</td><td>${e.cashier || "-"}</td><td>${e.label}</td><td>${e.desc || "-"}</td>
      <td style="text-align:right;color:${e.amount >= 0 ? "#059669" : "#dc2626"}">${signed(e.amount)}</td>
      <td style="text-align:right">${formatIDR(e.balance_after)}</td></tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Laporan Penjualan POS</title>
      <style>body{font-family:Arial,sans-serif;color:#1e293b;padding:24px}h1{font-size:20px;margin:0}.sub{color:#64748b;font-size:12px;margin:4px 0 14px}
      .cards{display:flex;gap:8px;flex-wrap:wrap}.card{flex:1;min-width:140px;border:1px solid #f5d0d8;border-radius:10px;padding:10px}.card b{display:block;font-size:15px;margin-top:4px}.lbl{font-size:10px;color:#64748b}
      .calc{font-size:12px;margin:14px 0;padding:10px;border:1px solid #f5d0d8;border-radius:10px;background:#fdf7f5}
      table{width:100%;border-collapse:collapse;font-size:11px;margin-top:8px}th{background:#fdf2f4;text-align:left;padding:6px;border:1px solid #f5d0d8}td{padding:6px;border:1px solid #f3e4e8}</style></head><body>
      <h1>Laporan Penjualan — Kiyana POS</h1>
      <div class="sub">Periode ${range.from} s/d ${range.to} • ${kasirLabel} • Dicetak ${new Date().toLocaleString("id-ID")}</div>
      <div class="cards">${cardHtml}</div>
      <div class="calc">Saldo Awal <b>${formatIDR(summary.saldo_awal)}</b> + Penjualan Tunai <b>${formatIDR(summary.cash_sales)}</b> + Uang Masuk Lain <b>${formatIDR(summary.cash_in)}</b> − Pengeluaran <b>${formatIDR(summary.cash_out)}</b> − Setoran <b>${formatIDR(summary.deposits)}</b> = Saldo Akhir <b>${formatIDR(summary.saldo_akhir)}</b></div>
      <table><thead><tr><th>Waktu</th><th>Kasir</th><th>Jenis</th><th>Keterangan</th><th style="text-align:right">Nominal</th><th style="text-align:right">Saldo</th></tr></thead><tbody>${rows}</tbody></table>
      </body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const heroes = [
    { icon: Wallet, label: "Uang Tunai Diterima", value: summary?.cash_sales, testid: "pos-rep-cash-sales" },
    { icon: Landmark, label: "Sudah Disetor", value: summary?.deposits, testid: "pos-rep-deposit" },
    { icon: Coins, label: "Masih di Kasir", value: summary?.saldo_berjalan, sub: "Saldo menurut sistem", testid: "pos-rep-running" },
  ];
  const secondary = [
    { icon: ArrowDownCircle, label: "Uang Masuk Lainnya", value: summary?.cash_in, testid: "pos-rep-cash-in" },
    { icon: ArrowUpCircle, label: "Pengeluaran Kas", value: summary?.cash_out, testid: "pos-rep-cash-out" },
    { icon: PiggyBank, label: "Saldo Kembalian (Tutup Kasir)", value: summary?.keep_total, sub: summary?.balancing ? `Terakhir ${formatIDR(summary.balancing.keep)} • ${summary.balancing.code}` : "Belum ada tutup kasir", testid: "pos-rep-keep" },
  ];
  const actions = [
    { key: "cash_in", label: "Uang Masuk", icon: ArrowDownCircle, testid: "pos-act-cash-in" },
    { key: "cash_out", label: "Pengeluaran", icon: ArrowUpCircle, testid: "pos-act-cash-out" },
    { key: "deposit", label: "Setor Tunai", icon: Landmark, testid: "pos-act-deposit" },
    { key: "refund_cash", label: "Refund Tunai", icon: RotateCcw, testid: "pos-act-refund" },
    { key: "adjustment", label: "Penyesuaian", icon: Scale, testid: "pos-act-adjustment" },
  ];
  const calcItems = summary ? [
    { label: "Saldo Awal", value: summary.saldo_awal },
    { op: "+", label: "Penjualan Tunai", value: summary.cash_sales },
    { op: "+", label: "Uang Masuk Lain", value: summary.cash_in },
    { op: "−", label: "Pengeluaran", value: summary.cash_out },
    { op: "−", label: "Setoran", value: summary.deposits },
  ] : [];
  const b = summary?.balancing;

  return (
    <div data-testid="pos-reports-page" className="-m-4 lg:-m-6 p-4 lg:p-6 bg-[#FBF7F4] min-h-full space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Laporan Penjualan</h1>
          <p className="text-sm text-slate-500">Posisi kas & mutasi cash drawer</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="pos-rep-pdf" onClick={exportPDF} className="h-9 px-3.5 rounded-lg bg-white border border-rose-200 text-xs font-medium text-rose-700 hover:bg-rose-50 flex items-center gap-1.5 transition-colors"><FileText className="w-3.5 h-3.5" /> PDF</button>
          <button data-testid="pos-rep-excel" onClick={exportExcel} className="h-9 px-3.5 rounded-lg bg-white border border-rose-200 text-xs font-medium text-rose-700 hover:bg-rose-50 flex items-center gap-1.5 transition-colors"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center bg-white border border-rose-100 rounded-lg p-0.5">
          {SEGMENTS.map((s) => (
            <button key={s.id} data-testid={`pos-rep-seg-${s.id}`} onClick={() => setSeg(s.id)}
              className={`h-8 px-3.5 rounded-md text-xs font-medium transition-colors ${seg === s.id ? "bg-rose-200/70 text-rose-800 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
              {s.label}
            </button>
          ))}
        </div>
        {seg === "day" && <input type="date" data-testid="pos-rep-day" value={day} onChange={(e) => setDay(e.target.value)} className={inp} />}
        {seg === "range" && (
          <div className="flex items-center gap-1.5">
            <input type="date" data-testid="pos-rep-from" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} className={inp} />
            <span className="text-slate-400 text-xs">s/d</span>
            <input type="date" data-testid="pos-rep-to" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} className={inp} />
          </div>
        )}
        {canPickCashier && (
          <select data-testid="pos-rep-cashier" value={cashierId} onChange={(e) => setCashierId(e.target.value)} className={inp}>
            <option value="">Semua Karyawan</option>
            {(summary?.cashiers || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <span className="text-[11px] text-slate-400">Periode {range.from} s/d {range.to} (GMT+07)</span>
      </div>

      {/* Kartu ringkasan utama */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {heroes.map((c) => (
          <div key={c.testid} data-testid={c.testid} className="bg-white rounded-2xl border border-rose-100 p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0"><c.icon className="w-5 h-5" /></div>
            <div className="min-w-0">
              <div className="text-xs text-slate-500">{c.label}</div>
              <div className="font-display font-bold text-xl text-slate-900 truncate">{summary ? formatIDR(c.value) : "…"}</div>
              {c.sub && <div className="text-[10px] text-slate-400">{c.sub}</div>}
            </div>
          </div>
        ))}
      </div>

      {/* Kartu ringkasan kedua */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {secondary.map((c) => (
          <div key={c.testid} data-testid={c.testid} className="bg-white rounded-2xl border border-rose-100 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center flex-shrink-0"><c.icon className="w-4.5 h-4.5" /></div>
            <div className="min-w-0">
              <div className="text-[11px] text-slate-500">{c.label}</div>
              <div className="font-display font-bold text-base text-slate-900 truncate">{summary ? formatIDR(c.value) : "…"}</div>
              {c.sub && <div className="text-[10px] text-slate-400 truncate">{c.sub}</div>}
            </div>
          </div>
        ))}
      </div>

      {/* Aksi mutasi kas */}
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => (
          <button key={a.key} data-testid={a.testid} onClick={() => setModal(a.key)}
            className="h-9 px-3.5 rounded-lg bg-white border border-rose-200 text-xs font-medium text-rose-700 hover:bg-rose-50 flex items-center gap-1.5 transition-colors">
            <a.icon className="w-3.5 h-3.5" /> {a.label}
          </button>
        ))}
      </div>

      {/* Perhitungan saldo kas */}
      <div className="bg-white rounded-2xl border border-rose-100 p-5" data-testid="pos-rep-calc">
        <div className="flex items-center gap-2 font-display font-semibold text-slate-900 text-sm mb-4">
          <span className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center"><Calculator className="w-4 h-4" /></span>
          Perhitungan Saldo Kas
        </div>
        {summary && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
            {calcItems.map((it) => (
              <div key={it.label} className="flex items-center gap-3">
                {it.op && <span className="text-rose-400 font-bold text-lg">{it.op}</span>}
                <div>
                  <div className="text-[10px] text-slate-500">{it.label}</div>
                  <div className="font-bold text-slate-900 text-sm">{formatIDR(it.value)}</div>
                </div>
              </div>
            ))}
            <span className="text-rose-400 font-bold text-lg">=</span>
            <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-2">
              <div className="text-[10px] text-rose-600">Saldo Akhir</div>
              <div className="font-bold text-rose-700 text-lg">{formatIDR(summary.saldo_akhir)}</div>
            </div>
          </div>
        )}
      </div>

      {/* Penyesuaian menunggu persetujuan (owner) */}
      {isOwner && (summary?.pending_adjustments || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-amber-200 p-5" data-testid="pos-pending-adj">
          <div className="font-display font-semibold text-slate-900 text-sm mb-2">Penyesuaian Menunggu Persetujuan</div>
          {summary.pending_adjustments.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-slate-50 last:border-0">
              <div>
                <div className="text-xs font-medium text-slate-800">{signed(m.amount)} • {m.note || "Tanpa keterangan"}</div>
                <div className="text-[10px] text-slate-400">{m.cashier_name} • {formatDateID(m.created_at)} {formatTimeID(m.created_at)}</div>
              </div>
              <div className="flex gap-1.5">
                <button data-testid={`pos-adj-approve-${m.id}`} onClick={() => reviewAdj(m.id, "approve")} className="h-8 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Setujui</button>
                <button data-testid={`pos-adj-reject-${m.id}`} onClick={() => reviewAdj(m.id, "reject")} className="h-8 px-3 rounded-lg bg-red-50 text-red-600 text-xs font-semibold flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Tolak</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mutasi + balancing */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        <div className="xl:col-span-2 bg-white rounded-2xl border border-rose-100 overflow-hidden" data-testid="pos-rep-mutations">
          <div className="px-5 py-4 border-b border-rose-50 flex items-center gap-2 font-display font-semibold text-slate-900 text-sm">
            <span className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center"><Wallet className="w-4 h-4" /></span>
            Rincian Mutasi Kas
          </div>
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-[11px] text-slate-500 border-b border-rose-50">
                  <th className="px-5 py-3 font-medium">Waktu</th>
                  <th className="px-4 py-3 font-medium">Kasir</th>
                  <th className="px-4 py-3 font-medium">Jenis</th>
                  <th className="px-4 py-3 font-medium">Keterangan</th>
                  <th className="px-4 py-3 font-medium text-right">Nominal</th>
                  <th className="px-4 py-3 font-medium text-right">Saldo</th>
                  <th className="px-4 py-3 font-medium w-10"></th>
                </tr>
              </thead>
              <tbody>
                {muts.map((e, i) => {
                  const meta = TYPE_META[e.type] || TYPE_META.cash_in;
                  return (
                    <tr key={`${e.ref}-${i}`} data-testid={`pos-mut-row-${i}`} className="border-b border-slate-50 hover:bg-rose-50/30">
                      <td className="px-5 py-3">
                        <div className="text-xs font-medium text-slate-800">{formatTimeID(e.time)}</div>
                        <div className="text-[10px] text-slate-400">{formatDateID(e.time)}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{e.cashier || "-"}</td>
                      <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span></td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-[220px] truncate">{e.desc || "-"}</td>
                      <td className={`px-4 py-3 text-right text-xs font-bold ${e.amount >= 0 ? "text-emerald-600" : "text-red-500"}`}>{signed(e.amount)}</td>
                      <td className="px-4 py-3 text-right text-xs text-slate-700">{formatIDR(e.balance_after)}</td>
                      <td className="px-4 py-3">
                        <button data-testid={`pos-mut-detail-${i}`} onClick={() => setDetail(e)} className="text-slate-300 hover:text-rose-500 transition-colors"><Eye className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  );
                })}
                {muts.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-xs text-slate-400">Belum ada mutasi pada periode ini.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-rose-100 p-5" data-testid="pos-rep-balancing">
          <div className="flex items-center gap-2 font-display font-semibold text-slate-900 text-sm mb-4">
            <span className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center"><Scale className="w-4 h-4" /></span>
            Balancing Tutup Kasir
          </div>
          {!b && <div className="text-xs text-slate-400 py-6 text-center">Belum ada tutup kasir pada periode ini.</div>}
          {b && (
            <>
              <Row label="Saldo Sistem">{formatIDR(b.saldo_sistem)}</Row>
              <Row label="Uang Fisik">{formatIDR(b.counted_cash)}</Row>
              <Row label="Disetorkan">{formatIDR(b.deposit)}</Row>
              <Row label="Disisakan (Kembalian)">{formatIDR(b.keep)}</Row>
              <div className={`mt-3 rounded-lg border px-3 py-2.5 text-center text-xs font-bold flex items-center justify-center gap-1.5 ${BAL_META[b.status]?.cls || BAL_META.balance.cls}`} data-testid="pos-bal-status">
                {b.status === "balance" ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {BAL_META[b.status]?.label || b.status}{b.status !== "balance" && ` • ${signed(b.selisih)}`}
              </div>
              <div className="text-[10px] text-slate-400 mt-2 text-center">{b.code} • {b.cashier} • {formatDateID(b.closed_at)} {formatTimeID(b.closed_at)}</div>
              <button data-testid="pos-bal-detail" onClick={() => setShiftDetail(b)} className="mt-3 w-full h-10 rounded-lg border border-rose-200 text-rose-700 text-xs font-semibold hover:bg-rose-50 flex items-center justify-center gap-1.5 transition-colors">
                <Eye className="w-3.5 h-3.5" /> Lihat Detail Shift
              </button>
            </>
          )}
        </div>
      </div>

      {/* Riwayat shift */}
      <div className="bg-white rounded-2xl border border-rose-100 overflow-hidden" data-testid="pos-rep-shifts">
        <div className="px-5 py-4 border-b border-rose-50 font-display font-semibold text-slate-900 text-sm">Riwayat Tutup Kasir & Shift</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className="text-left text-[11px] text-slate-500 border-b border-rose-50 bg-rose-50/40">
                <th className="px-5 py-3 font-medium">Shift</th>
                <th className="px-4 py-3 font-medium">Kasir</th>
                <th className="px-4 py-3 font-medium text-right">Modal Awal</th>
                <th className="px-4 py-3 font-medium text-right">Tunai</th>
                <th className="px-4 py-3 font-medium text-right">Non-Tunai</th>
                <th className="px-4 py-3 font-medium text-right">Saldo Sistem</th>
                <th className="px-4 py-3 font-medium text-right">Uang Fisik</th>
                <th className="px-4 py-3 font-medium text-right">Setoran</th>
                <th className="px-4 py-3 font-medium text-right">Disisakan</th>
                <th className="px-4 py-3 font-medium">Balancing</th>
              </tr>
            </thead>
            <tbody>
              {shifts.map((x) => {
                const selisih = x.balancing ?? 0;
                const st = x.balancing_status || (x.status === "closed" ? (selisih === 0 ? "balance" : selisih > 0 ? "lebih" : "kurang") : null);
                return (
                  <tr key={x.id} data-testid={`pos-shift-${x.code}`} className="border-b border-slate-50 hover:bg-rose-50/30">
                    <td className="px-5 py-3">
                      <div className="text-xs font-medium text-slate-800">{x.code}</div>
                      <div className="text-[10px] text-slate-400">{formatDateID(x.opened_at)} {formatTimeID(x.opened_at)}{x.closed_at ? ` — ${formatTimeID(x.closed_at)}` : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{x.cashier_name}</td>
                    <td className="px-4 py-3 text-right text-xs">{formatIDR(x.starting_cash)}</td>
                    <td className="px-4 py-3 text-right text-xs">{x.status === "closed" ? formatIDR(x.cash_sales) : "—"}</td>
                    <td className="px-4 py-3 text-right text-xs">{x.status === "closed" ? formatIDR(x.noncash_sales) : "—"}</td>
                    <td className="px-4 py-3 text-right text-xs">{x.status === "closed" ? formatIDR(x.saldo_sistem ?? x.expected_cash) : "—"}</td>
                    <td className="px-4 py-3 text-right text-xs">{x.status === "closed" ? formatIDR(x.counted_cash) : "—"}</td>
                    <td className="px-4 py-3 text-right text-xs font-semibold">{x.status === "closed" ? formatIDR(x.deposit_amount) : "—"}</td>
                    <td className="px-4 py-3 text-right text-xs font-semibold">{x.status === "closed" ? formatIDR(x.keep_amount ?? x.drawer_left) : "—"}</td>
                    <td className="px-4 py-3">
                      {x.status === "open" ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">Berjalan</span>
                      ) : (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${BAL_META[st]?.cls || BAL_META.balance.cls}`}>
                          {st === "balance" ? "Balance" : st === "lebih" ? `Lebih ${signed(selisih)}` : `Kurang ${signed(selisih)}`}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {shifts.length === 0 && (
                <tr><td colSpan={10} className="px-5 py-10 text-center text-xs text-slate-400">Belum ada shift pada periode ini.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Riwayat petugas POS + serah terima */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <div className="bg-white rounded-2xl border border-rose-100 overflow-hidden" data-testid="pos-rep-attendants">
          <div className="px-5 py-4 border-b border-rose-50 font-display font-semibold text-slate-900 text-sm">Riwayat Petugas POS</div>
          <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
            <table className="w-full text-sm min-w-[460px]">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-[11px] text-slate-500 border-b border-rose-50">
                  <th className="px-5 py-3 font-medium">Petugas</th>
                  <th className="px-4 py-3 font-medium">Login</th>
                  <th className="px-4 py-3 font-medium">Kunci</th>
                  <th className="px-4 py-3 font-medium">Metode</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((x) => (
                  <tr key={x.id} className="border-b border-slate-50">
                    <td className="px-5 py-2.5 text-xs font-medium text-slate-800">{x.attendant_name}<span className="block text-[10px] text-slate-400 capitalize">{x.attendant_role}</span></td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{formatDateID(x.login_at)} {formatTimeID(x.login_at)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{x.logout_at ? `${formatDateID(x.logout_at)} ${formatTimeID(x.logout_at)}` : <span className="text-emerald-600 font-semibold">Aktif</span>}</td>
                    <td className="px-4 py-2.5"><span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-600">{x.method === "card" ? "ID Card" : "PIN"}</span></td>
                  </tr>
                ))}
                {sessions.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-xs text-slate-400">Belum ada aktivitas petugas pada periode ini.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-rose-100 overflow-hidden" data-testid="pos-rep-handovers">
          <div className="px-5 py-4 border-b border-rose-50 font-display font-semibold text-slate-900 text-sm">Riwayat Serah Terima Cash Drawer</div>
          <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-[11px] text-slate-500 border-b border-rose-50">
                  <th className="px-5 py-3 font-medium">Waktu</th>
                  <th className="px-4 py-3 font-medium">Penanggung Jawab</th>
                  <th className="px-4 py-3 font-medium text-right">Saldo Sistem</th>
                  <th className="px-4 py-3 font-medium text-right">Uang Fisik</th>
                  <th className="px-4 py-3 font-medium text-right">Selisih</th>
                </tr>
              </thead>
              <tbody>
                {handovers.map((h) => (
                  <tr key={h.id} className="border-b border-slate-50">
                    <td className="px-5 py-2.5">
                      <div className="text-xs font-medium text-slate-800">{formatTimeID(h.created_at)}</div>
                      <div className="text-[10px] text-slate-400">{formatDateID(h.created_at)} • {h.shift_code}</div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{h.old_name} <span className="text-slate-400">→</span> <b className="text-slate-800">{h.new_name}</b>{h.note ? <span className="block text-[10px] text-slate-400">{h.note}</span> : null}</td>
                    <td className="px-4 py-2.5 text-right text-xs">{formatIDR(h.saldo_sistem)}</td>
                    <td className="px-4 py-2.5 text-right text-xs">{formatIDR(h.physical_cash)}</td>
                    <td className={`px-4 py-2.5 text-right text-xs font-bold ${h.selisih === 0 ? "text-emerald-600" : "text-red-500"}`}>{h.selisih === 0 ? "Balance" : signed(h.selisih)}</td>
                  </tr>
                ))}
                {handovers.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-xs text-slate-400">Belum ada serah terima pada periode ini.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal mutasi */}
      {modal && <MutModal kind={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}

      {/* Modal detail mutasi */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="pos-mut-detail-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-slate-900">Detail Mutasi</div>
              <button data-testid="pos-mut-detail-close" onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <Row label="Waktu">{formatDateID(detail.time)} {formatTimeID(detail.time)}</Row>
            <Row label="Kasir">{detail.cashier || "-"}</Row>
            <Row label="Jenis"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${(TYPE_META[detail.type] || TYPE_META.cash_in).cls}`}>{detail.label}</span></Row>
            <Row label="Keterangan">{detail.desc || "-"}</Row>
            <Row label="Nominal"><span className={detail.amount >= 0 ? "text-emerald-600 font-bold" : "text-red-500 font-bold"}>{signed(detail.amount)}</span></Row>
            <Row label="Saldo Setelah Transaksi">{formatIDR(detail.balance_after)}</Row>
            <Row label="Referensi">{detail.ref_kind === "txn" ? "Transaksi penjualan" : detail.ref_kind === "shift" ? "Shift kasir" : "Mutasi manual"}</Row>
          </div>
        </div>
      )}

      {/* Modal detail shift */}
      {shiftDetail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShiftDetail(null)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="pos-shift-detail-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-slate-900">Detail Shift {shiftDetail.code}</div>
              <button data-testid="pos-shift-detail-close" onClick={() => setShiftDetail(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <Row label="Kasir">{shiftDetail.cashier}</Row>
            <Row label="Ditutup">{formatDateID(shiftDetail.closed_at)} {formatTimeID(shiftDetail.closed_at)}</Row>
            <Row label="Saldo Sistem">{formatIDR(shiftDetail.saldo_sistem)}</Row>
            <Row label="Uang Fisik (Hitungan)">{formatIDR(shiftDetail.counted_cash)}</Row>
            <Row label="Disetorkan">{formatIDR(shiftDetail.deposit)}</Row>
            <Row label="Disisakan untuk Kembalian">{formatIDR(shiftDetail.keep)}</Row>
            <Row label="Selisih"><span className={shiftDetail.selisih === 0 ? "text-emerald-600 font-bold" : "text-red-500 font-bold"}>{signed(shiftDetail.selisih)}</span></Row>
            <div className={`mt-3 rounded-lg border px-3 py-2.5 text-center text-xs font-bold ${BAL_META[shiftDetail.status]?.cls || BAL_META.balance.cls}`}>
              {BAL_META[shiftDetail.status]?.label || shiftDetail.status}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
